import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { previewVisaDocument } from "utils/visaDocumentService";

const sanitizeFileName = (title) =>
  `${
    String(title || "document")
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase() || "document"
  }-preview.pdf`;

/**
 * The document as the client will receive it, rendered from the answers currently in the send
 * dialog. Nothing is saved and no email goes out — this is the admin proof-reading their own work
 * before it leaves the building.
 *
 * The blob URL is built on open and revoked on close, so a preview never outlives the dialog that
 * asked for it.
 */
const DocumentPreviewDialog = ({
  open,
  onClose,
  documentTitle,
  payload,
  onConfirmSend,
  sending,
}) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !payload) return undefined;

    let objectUrl = null;
    let cancelled = false;

    setLoading(true);
    setError("");
    previewVisaDocument(payload)
      .then((url) => {
        objectUrl = url;
        // The dialog closed while the render was in flight — drop the blob rather than leak it.
        if (cancelled) {
          window.URL.revokeObjectURL(url);
          return;
        }
        setBlobUrl(url);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Could not build the preview");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      setBlobUrl(null);
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payload]);

  const handleDownload = () => {
    if (!blobUrl) return;
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = sanitizeFileName(documentTitle);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle className="font-semibold">Preview "{documentTitle}"</DialogTitle>
      <DialogContent dividers sx={{ p: 0, height: "72vh" }}>
        {loading && (
          <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ height: "100%" }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Building the preview…
            </Typography>
          </Stack>
        )}

        {!loading && error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {!loading && !error && blobUrl && (
          <iframe
            title={`Preview of ${documentTitle}`}
            src={blobUrl}
            style={{ width: "100%", height: "100%", border: 0, display: "block" }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>
          This is what the client will fill in and sign. Nothing has been sent yet.
        </Typography>
        <Button onClick={handleDownload} disabled={!blobUrl} startIcon={<DownloadIcon />}>
          Download PDF
        </Button>
        <Button onClick={onClose} disabled={sending}>
          Close
        </Button>
        {onConfirmSend && (
          <Button variant="contained" onClick={onConfirmSend} disabled={sending || Boolean(error)}>
            {sending ? "Sending…" : "Send link"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewDialog;
